/**
 * Conditions — the one module that decides whether a respondent meets a variable's condition, and
 * who a variable's own table is based on (M4S-5777: defects 2 and 3, X23, X47).
 * Spec: docs/pdlc/crosstabs-conditions.md.
 *
 * Two rules changed, and a saved crosstab is never changed silently, so a variable carries the
 * rules it was saved under (`CustomVariable.rules`):
 *
 * - **≠ (defect 2).** A condition on a question the respondent did not answer is false for every
 *   operator. "Not aware" means "answered, and not Aware"; somebody who was never asked is in
 *   neither group, so Aware + Not aware is the question's answerers, never Total. A variable whose
 *   base is `'everyone'` keeps the wider meaning: ≠ also counts someone who did not answer.
 * - **<, ≤, >, ≥ (defect 3, decision 4).** On a number question (`'value'`) they compare the value
 *   answered. On a coded list (`'order'`) they compare the answers' order in the survey, so
 *   "Income is before $100,000" counts the bands listed before it, whatever codes they carry. Don't
 *   know, N/A, None of these, Other and Prefer not to say are on no order and never qualify
 *   (decision C3). Open ends, ranked, matrix and market questions (`'none'`) take no comparison.
 *
 * A variable without the stamp is computed exactly as before (≠ counts the unasked, a comparison
 * reads the first code as a number, its own table is based on the whole column) and is reported by
 * `legacyRulesOf`, so the workspace can say so. Only a save stamps a variable (`withConditionRules`).
 *
 * The module is pure. Compute resolves each variable once (`resolveConditions`, which attaches the
 * answer order or the number a comparison reads) and evaluates it here. Nothing else evaluates a
 * condition: `conditions.guard.test.ts` fails on a second copy.
 *
 * M4S-5777 (X46, X49): the same module reads the conditions a person builds by picking answers:
 * any of / none of several answers (`in`, `notIn`), a range (`between`, both bounds inclusive),
 * and explicit groups, (A OR B) AND NOT (C), on a bucket (`groups`). An absent field means what it
 * always meant, so no saved variable moves; the new fields are written only by an edit, which
 * stamps the variable. `rangeCoverage` says where Bucketing's number ranges leave a gap or overlap.
 * Spec: docs/pdlc/crosstabs-variables.md.
 */

import type {
    ConditionGroup,
    ConditionOperator,
    CustomVariable,
    RangeSegment,
    VariableBucket,
    VariableCondition,
} from '@/app/(private)/ai/components/Crosstabs/crosstab-types';

import { isGeographyQuestion } from './geography';
import { isRankedQuestion } from './ranked';
import { anyNumber, entryCellOf } from './respondent-unit';
import type { CrosstabQuestionDto } from './question-formatter';
import { answersOf, isOffScaleLabel } from './scale';

// ── The stamp ─────────────────────────────────────────────────────────────────

/** The rules a variable saved today carries. Bump only with a migration path in `legacyRulesOf`. */
export const CONDITION_RULES = 2 as const;

export type VariableBase = 'answered' | 'everyone';

/** A respondent's answers, keyed by upper-cased response column, as compute parses them. */
export type Answers = Record<string, Set<string> | undefined>;

/** How a variable's conditions are read: under today's rules or the earlier ones, and on which base. */
export interface ConditionRules {
    /** Saved under `CONDITION_RULES`. False: the earlier rules, kept for a saved variable. */
    current: boolean;
    /** The variable's base is everyone, so ≠ also counts someone who did not answer. */
    everyone: boolean;
}

export const LEGACY_RULES: ConditionRules = { current: false, everyone: true };
export const CURRENT_RULES: ConditionRules = { current: true, everyone: false };

export function isCurrentRules(x: { rules?: number } | null | undefined): boolean {
    return x?.rules === CONDITION_RULES;
}

/** Who a variable's own table counts. An unstamped variable keeps the whole column. */
export function variableBaseOf(cv: Pick<CustomVariable, 'rules' | 'base'>): VariableBase {
    if (!isCurrentRules(cv)) return 'everyone';
    return cv.base === 'everyone' ? 'everyone' : 'answered';
}

export function conditionRulesOf(cv: Pick<CustomVariable, 'rules' | 'base'>): ConditionRules {
    if (!isCurrentRules(cv)) return LEGACY_RULES;
    return { current: true, everyone: variableBaseOf(cv) === 'everyone' };
}

/**
 * The rules a variable's banner columns count under. A column counts its own members, so the base
 * choice (which is about a variable's own table) never reaches it: a stamped variable carrying
 * `base: 'everyone'` still counts ≠ among answerers on the banner.
 */
export function bannerRulesOf(cv: Pick<CustomVariable, 'rules'>): ConditionRules {
    return isCurrentRules(cv) ? CURRENT_RULES : LEGACY_RULES;
}

/**
 * The variable as a save writes it: stamped with today's rules, with its base. `'answered'` is the
 * default and is not written, so a variable that never chose a base stays as small as it was.
 */
export function withConditionRules<T extends CustomVariable>(cv: T, base?: VariableBase): T {
    const rest: T = { ...cv };
    delete rest.base;
    const chosen = base ?? cv.base;
    return {
        ...rest,
        rules: CONDITION_RULES,
        ...(chosen === 'everyone' ? { base: 'everyone' as const } : {}),
    };
}

// ── What a comparison reads ───────────────────────────────────────────────────

export type ComparisonKind = 'value' | 'order' | 'none';

export const COMPARISON_OPERATORS: ReadonlySet<VariableCondition['operator']> = new Set([
    'lt',
    'gt',
    'lte',
    'gte',
]);

export function isComparison(op: VariableCondition['operator']): boolean {
    return COMPARISON_OPERATORS.has(op);
}

/** X46: "≠" and "none of" read the unasked by the variable's rules: true where the base is everyone. */
export function isNegative(op: ConditionOperator): boolean {
    return op === 'neq' || op === 'notIn';
}

/** X46: a condition whose meaning depends on the answers' order or value: a comparison or a range. */
export function readsOrderOrValue(op: ConditionOperator): boolean {
    return isComparison(op) || op === 'between';
}

/** The answers an `in` / `notIn` condition names, trimmed, without blanks or repeats. */
export function valuesOf(cond: Pick<VariableCondition, 'values' | 'value'>): string[] {
    const raw: unknown[] = Array.isArray(cond.values)
        ? cond.values
        : cond.value
          ? [cond.value]
          : [];
    const out: string[] = [];
    for (const v of raw) {
        const t = typeof v === 'string' ? v.trim() : typeof v === 'number' ? String(v) : '';
        if (t && !out.includes(t)) out.push(t);
    }
    return out;
}

/**
 * Whether a variable's conditions can mean something different under the earlier rules: a ≠, a
 * none-of, a NOT group, a comparison or a range. Equality alone reads the same under both.
 */
export function differsUnderEarlierRules(cv: Pick<CustomVariable, 'buckets'>): boolean {
    return cv.buckets.some(
        (b) =>
            b.conditions.some((c) => isNegative(c.operator) || readsOrderOrValue(c.operator)) ||
            hasNotGroup(b),
    );
}

// ── Groups (X46) ──────────────────────────────────────────────────────────────

/** One group of a bucket as it is read: its join, whether it is negated, and its conditions. */
export interface BucketGroup<C extends VariableCondition = VariableCondition> {
    join: 'all' | 'any';
    not: boolean;
    conditions: C[];
}

/** Whether a bucket is grouped (`groups` holds at least one group). */
export function isGrouped(bucket: Pick<VariableBucket, 'groups'>): boolean {
    return Array.isArray(bucket.groups) && bucket.groups.length > 0;
}

/** Whether any group of a bucket is negated. */
export function hasNotGroup(bucket: Pick<VariableBucket, 'groups'>): boolean {
    return isGrouped(bucket) && bucket.groups!.some((g) => g?.not === true);
}

/** The group a condition sits in: its `group` when it names one of `count`, else group 0. */
function groupIndexOf(c: VariableCondition, count: number): number {
    const g = c.group;
    return typeof g === 'number' && Number.isInteger(g) && g >= 0 && g < count ? g : 0;
}

/**
 * A grouped bucket's groups, in order, each with its conditions in bucket order. Groups with no
 * condition are left out. Null for a bucket that is not grouped: it is read as the fold.
 */
export function bucketGroups<C extends VariableCondition>(
    bucket: Omit<VariableBucket, 'conditions'> & { conditions: C[] },
): BucketGroup<C>[] | null {
    if (!isGrouped(bucket)) return null;
    const defs = bucket.groups as ConditionGroup[];
    const groups: BucketGroup<C>[] = defs.map((g) => ({
        join: g?.join === 'all' ? 'all' : 'any',
        not: g?.not === true,
        conditions: [],
    }));
    for (const c of bucket.conditions) groups[groupIndexOf(c, groups.length)].conditions.push(c);
    return groups.filter((g) => g.conditions.length > 0);
}

/** How a grouped bucket joins its groups: AND unless it says OR. */
export function groupJoinOf(bucket: Pick<VariableBucket, 'groupJoin'>): 'all' | 'any' {
    return bucket.groupJoin === 'any' ? 'any' : 'all';
}

/** A condition without the fold's connector or a group index. */
function ungrouped(c: VariableCondition): VariableCondition {
    const out: VariableCondition = { ...c };
    delete out.nextJoin;
    delete out.group;
    return out;
}

/**
 * The fold of a bucket written as groups that count exactly the same people, or null when the fold
 * cannot be written as one level of groups. The fold reads `((c1 op c2) op c3)…`, so it is one
 * group when every connector is the same; ORs then ANDs is `(c1 OR … OR ck) AND c(k+1) AND …`; and
 * ANDs then ORs is `(c1 AND … AND ck) OR c(k+1) OR …`. Anything else (an OR after an AND after an
 * OR) would need groups inside groups. A bucket already grouped comes back as it is.
 */
export function chainToGroups(bucket: VariableBucket): VariableBucket | null {
    if (isGrouped(bucket)) return bucket;
    const n = bucket.conditions.length;
    if (n === 0) return null;
    const joins = bucket.conditions.slice(0, -1).map((c) => c.nextJoin ?? bucket.join);
    const build = (
        groups: ConditionGroup[],
        index: (i: number) => number,
        groupJoin: 'all' | 'any',
    ): VariableBucket => ({
        label: bucket.label,
        join: bucket.join,
        conditions: bucket.conditions.map((c, i) => ({ ...ungrouped(c), group: index(i) })),
        ...(bucket.nextJoin ? { nextJoin: bucket.nextJoin } : {}),
        groups,
        groupJoin,
    });
    if (joins.every((j) => j === joins[0])) {
        return build([{ join: joins[0] ?? bucket.join }], () => 0, 'all');
    }
    // A run of the first connector, then only the other one.
    const first = joins[0];
    const k = joins.findIndex((j) => j !== first);
    if (joins.slice(k).some((j) => j === first)) return null;
    // Conditions 0..k are group 0, joined by `first`; each later one is a group of its own, and
    // the groups are joined by the other connector.
    const other: 'all' | 'any' = first === 'any' ? 'all' : 'any';
    const groups: ConditionGroup[] = [{ join: first }];
    for (let i = k + 1; i < n; i++) groups.push({ join: other });
    return build(groups, (i) => (i <= k ? 0 : i - k), other);
}

/**
 * A grouped bucket written back as the fold, when one group with no NOT holds every condition
 * (the fold of one connector reads the same), else null.
 */
export function groupsToChain(bucket: VariableBucket): VariableBucket | null {
    const groups = bucketGroups(bucket);
    if (!groups) return bucket;
    if (groups.length !== 1 || groups[0].not) return null;
    const out: VariableBucket = {
        label: bucket.label,
        join: groups[0].join,
        conditions: bucket.conditions.map(ungrouped),
    };
    if (bucket.nextJoin) out.nextJoin = bucket.nextJoin;
    return out;
}

/** Question types whose answers are numbers: a comparison reads the value. */
const VALUE_TYPES = new Set(['intensity_scale', 'radio intensity scale']);
// Ranked questions are named by `isRankedQuestion` (ranked.ts, the one home for that rule).
const NONE_TYPES = new Set(['open_end', 'matrix', 'grid', 'intensity scale matrix']);

/** Enough of a question to say what a comparison on it reads. Fits the catalog and the DTO alike. */
export interface ComparisonQuestion {
    qid: string;
    responseColumnId?: string;
    questionType?: string;
    isOpenEnd?: boolean;
    matrixOptions?: Record<string, string[]>;
}

/**
 * What <, ≤, > and ≥ mean on a question: its value (`'value'`, an intensity scale), its answers'
 * order (`'order'`, a coded list), or nothing (`'none'`: open ends, ranked, matrix and market
 * questions, and a question not in the catalog).
 */
export function comparisonKindOf(q: ComparisonQuestion | null | undefined): ComparisonKind {
    if (!q) return 'none';
    const type = (q.questionType ?? '').toLowerCase();
    if (q.isOpenEnd || NONE_TYPES.has(type) || isRankedQuestion({ questionType: type })) {
        return 'none';
    }
    if (Object.keys(q.matrixOptions ?? {}).length > 0) return 'none';
    if (isGeographyQuestion(q)) return 'none';
    if (VALUE_TYPES.has(type)) return 'value';
    return 'order';
}

/** The words an operator reads as on a question of this kind (decision C4). */
export function operatorLabel(op: VariableCondition['operator'], kind: ComparisonKind): string {
    if (kind === 'order') {
        switch (op) {
            case 'lt':
                return 'is before';
            case 'lte':
                return 'is at or before';
            case 'gt':
                return 'is after';
            case 'gte':
                return 'is at or after';
        }
    }
    switch (op) {
        case 'eq':
            return '=';
        case 'neq':
            return '≠';
        case 'lt':
            return '<';
        case 'lte':
            return '≤';
        case 'gt':
            return '>';
        case 'gte':
            return '≥';
        case 'in':
            return 'is any of';
        case 'notIn':
            return 'is none of';
        case 'between':
            return 'is between';
    }
}

/**
 * X46: whether a condition is filled in: a question, and an answer (=, ≠, a comparison), at least
 * one answer (any of, none of), or at least one end (a range).
 */
export function conditionComplete(c: VariableCondition): boolean {
    if (!c.questionId?.trim()) return false;
    if (c.operator === 'in' || c.operator === 'notIn') return valuesOf(c).length > 0;
    if (c.operator === 'between') return !!(boundOf(c.min) || boundOf(c.max));
    return !!String(c.value ?? '').trim();
}

/** The answers an order comparison can name, in survey order: every answer that is on the order. */
export function orderedAnswers<T extends { label: string }>(answers: readonly T[]): T[] {
    return answers.filter((a) => !isOffScaleLabel(a.label));
}

/** A comparison resolved against its question once, per compute. */
export type Comparison =
    | { kind: 'order'; rankOf: ReadonlyMap<string, number>; at: number | null }
    | {
          kind: 'value';
          threshold: number;
          min: number | null;
          max: number | null;
          offScale: ReadonlySet<string>;
      }
    | { kind: 'none' };

/**
 * A condition as compute evaluates it. `anyOf` is its value's whole option when the option carries
 * several codes (a merged survey wave, option-codes.ts). `cmp` is what a comparison reads, set by
 * `resolveConditions`.
 */
export type ResolvedCondition = VariableCondition & {
    anyOf?: string[];
    cmp?: Comparison;
    /** X46: what a `between` reads, set by `resolveConditions`. */
    span?: Span;
};

/** A `between` resolved against its question: the order positions, or the values, it spans. */
export type Span =
    | {
          kind: 'order';
          rankOf: ReadonlyMap<string, number>;
          lo: number | null;
          hi: number | null;
          /** False when a bound names no answer on the order, or both are open: counts no one. */
          ok: boolean;
      }
    | {
          kind: 'value';
          lo: number;
          hi: number;
          min: number | null;
          max: number | null;
          offScale: ReadonlySet<string>;
      }
    | { kind: 'none' };

function rankMapOf(q: CrosstabQuestionDto): Map<string, number> {
    const ranks = new Map<string, number>();
    orderedAnswers(answersOf(q)).forEach((a, i) => {
        for (const code of a.codes) {
            const key = code.trim().toUpperCase();
            if (!ranks.has(key)) ranks.set(key, i);
        }
    });
    return ranks;
}

function offScaleCodesOf(q: CrosstabQuestionDto): Set<string> {
    return new Set(
        answersOf(q)
            .filter((a) => isOffScaleLabel(a.label))
            .flatMap((a) => a.codes.map((c) => c.trim().toUpperCase())),
    );
}

/** The question a condition names: by its qid, or by the response column a wave variant reads. */
export function questionLookup(
    questions: readonly CrosstabQuestionDto[],
): (qid: string) => CrosstabQuestionDto | undefined {
    const byQid = new Map<string, CrosstabQuestionDto>();
    for (const q of questions) byQid.set(q.qid.toUpperCase(), q);
    for (const q of questions) {
        const col = q.responseColumnId?.toUpperCase();
        if (col && !byQid.has(col)) byQid.set(col, q);
    }
    return (qid) => byQid.get(qid.trim().toUpperCase());
}

type ResolveCache = Map<string, Map<string, number> | Set<string>>;

function cachedRanks(q: CrosstabQuestionDto, cache: ResolveCache): Map<string, number> {
    const key = `r:${q.qid.toUpperCase()}`;
    let ranks = cache.get(key) as Map<string, number> | undefined;
    if (!ranks) {
        ranks = rankMapOf(q);
        cache.set(key, ranks);
    }
    return ranks;
}

function cachedOffScale(q: CrosstabQuestionDto, cache: ResolveCache): Set<string> {
    const key = `o:${q.qid.toUpperCase()}`;
    let offScale = cache.get(key) as Set<string> | undefined;
    if (!offScale) {
        offScale = offScaleCodesOf(q);
        cache.set(key, offScale);
    }
    return offScale;
}

function resolveComparison(
    cond: VariableCondition,
    q: CrosstabQuestionDto | undefined,
    cache: ResolveCache,
): Comparison {
    const kind = comparisonKindOf(q);
    if (!q || kind === 'none') return { kind: 'none' };
    if (kind === 'order') {
        const ranks = cachedRanks(q, cache);
        return { kind, rankOf: ranks, at: ranks.get(cond.value.trim().toUpperCase()) ?? null };
    }
    return {
        kind,
        threshold: parseFloat(cond.value),
        min: q.min ?? null,
        max: q.max ?? null,
        offScale: cachedOffScale(q, cache),
    };
}

/** A bound as written: '' or absent is open. */
function boundOf(raw: unknown): string {
    return typeof raw === 'string' ? raw.trim() : typeof raw === 'number' ? String(raw) : '';
}

/**
 * X46: what a `between` reads on its question. On a coded list each bound is an answer on the
 * order, and a bound that names none (a typed code, Don't know) makes the range count no one; on a
 * number question each bound is a number. A question that takes no comparison takes no range.
 */
function resolveSpan(
    cond: VariableCondition,
    q: CrosstabQuestionDto | undefined,
    cache: ResolveCache,
): Span {
    const kind = comparisonKindOf(q);
    if (!q || kind === 'none') return { kind: 'none' };
    const lo = boundOf(cond.min);
    const hi = boundOf(cond.max);
    if (kind === 'order') {
        const ranks = cachedRanks(q, cache);
        const loRank = lo ? (ranks.get(lo.toUpperCase()) ?? null) : null;
        const hiRank = hi ? (ranks.get(hi.toUpperCase()) ?? null) : null;
        const ok = !!(lo || hi) && (!lo || loRank != null) && (!hi || hiRank != null);
        return { kind, rankOf: ranks, lo: loRank, hi: hiRank, ok };
    }
    return {
        kind,
        lo: lo ? parseFloat(lo) : -Infinity,
        hi: hi ? parseFloat(hi) : Infinity,
        min: q.min ?? null,
        max: q.max ?? null,
        offScale: cachedOffScale(q, cache),
    };
}

/**
 * Attaches to each comparison what it reads (the answer order or the value), once per compute.
 * Equality conditions are returned as they are; variables with no comparison are returned as they
 * are, so the dirty check and every reader see the same objects.
 */
export function resolveConditions(
    cvs: CustomVariable[],
    questions: readonly CrosstabQuestionDto[],
): CustomVariable[] {
    const find = questionLookup(questions);
    const cache = new Map<string, Map<string, number> | Set<string>>();
    return cvs.map((cv) => resolveVariable(cv, find, cache));
}

function resolveVariable(
    cv: CustomVariable,
    find: (qid: string) => CrosstabQuestionDto | undefined,
    cache: ResolveCache,
): CustomVariable {
    if (cv.kind !== 'logic' && cv.kind !== 'categorical-bucketing') return cv;
    if (!cv.buckets.some((b) => b.conditions.some((c) => readsOrderOrValue(c.operator)))) {
        return cv;
    }
    return { ...cv, buckets: resolveBuckets(cv.buckets, find, cache) };
}

function resolveBuckets(
    buckets: VariableBucket[],
    find: (qid: string) => CrosstabQuestionDto | undefined,
    cache: ResolveCache,
): VariableBucket[] {
    return buckets.map((b) => ({
        ...b,
        conditions: b.conditions.map((cond): ResolvedCondition => {
            if (isComparison(cond.operator)) {
                return { ...cond, cmp: resolveComparison(cond, find(cond.questionId), cache) };
            }
            if (cond.operator === 'between') {
                return { ...cond, span: resolveSpan(cond, find(cond.questionId), cache) };
            }
            return cond;
        }),
    }));
}

/** Buckets with their comparisons resolved: a weighting group's, which is not a variable. */
export function resolveBucketConditions(
    buckets: VariableBucket[],
    questions: readonly CrosstabQuestionDto[],
): VariableBucket[] {
    return resolveBuckets(buckets, questionLookup(questions), new Map());
}

// ── Evaluating ────────────────────────────────────────────────────────────────

function compare(op: VariableCondition['operator'], a: number, b: number): boolean {
    if (op === 'lt') return a < b;
    if (op === 'gt') return a > b;
    if (op === 'lte') return a <= b;
    return a >= b;
}

/**
 * The earlier comparison: the first code held, read as a number, against the typed value.
 *
 * M4S-5777 (diary D3; round-2 I1): a diary person's merged cell passes when any entry's answer
 * does. Only a diary cell: an ordinary multi-answer cell under the earlier rules keeps the
 * first-number reading it was saved with, so an unstamped variable's numbers do not move.
 */
function legacyComparison(selected: Set<string>, cond: VariableCondition): boolean {
    const threshold = parseFloat(cond.value);
    if (isNaN(threshold)) return false;
    if (entryCellOf(selected)) {
        return anyNumber(selected, (num) => compare(cond.operator, num, threshold));
    }
    const num = parseFloat([...selected][0]);
    return !isNaN(num) && compare(cond.operator, num, threshold);
}

/** Today's comparison: any answer held that is on the order (or a value on the scale) qualifies. */
function currentComparison(selected: Set<string>, cond: ResolvedCondition): boolean {
    const cmp = cond.cmp;
    if (!cmp || cmp.kind === 'none') return false;
    if (cmp.kind === 'order') {
        if (cmp.at == null) return false;
        for (const token of selected) {
            const rank = cmp.rankOf.get(token);
            if (rank != null && compare(cond.operator, rank, cmp.at)) return true;
        }
        return false;
    }
    if (!Number.isFinite(cmp.threshold)) return false;
    for (const token of selected) {
        if (cmp.offScale.has(token)) continue;
        const n = parseFloat(token);
        if (!Number.isFinite(n)) continue;
        if (cmp.min != null && n < cmp.min) continue;
        if (cmp.max != null && n > cmp.max) continue;
        if (compare(cond.operator, n, cmp.threshold)) return true;
    }
    return false;
}

/** X46: a range under the earlier reading: any number held within the typed bounds. */
function legacySpan(selected: Set<string>, cond: VariableCondition): boolean {
    const lo = boundOf(cond.min);
    const hi = boundOf(cond.max);
    if (!lo && !hi) return false;
    const min = lo ? parseFloat(lo) : -Infinity;
    const max = hi ? parseFloat(hi) : Infinity;
    if (isNaN(min) || isNaN(max)) return false;
    // As `legacyComparison` (I1): a diary person passes on any entry; any other cell, its first.
    if (entryCellOf(selected)) return anyNumber(selected, (num) => num >= min && num <= max);
    const num = firstNumberOf(selected);
    return num != null && num >= min && num <= max;
}

/** X46: a range under today's rules: an answer on the order, or a value on the scale, within it. */
function currentSpan(selected: Set<string>, cond: ResolvedCondition): boolean {
    const span = cond.span;
    if (!span || span.kind === 'none') return false;
    if (span.kind === 'order') {
        if (!span.ok) return false;
        for (const token of selected) {
            const rank = span.rankOf.get(token);
            if (rank == null) continue;
            if (span.lo != null && rank < span.lo) continue;
            if (span.hi != null && rank > span.hi) continue;
            return true;
        }
        return false;
    }
    if (isNaN(span.lo) || isNaN(span.hi)) return false;
    for (const token of selected) {
        if (span.offScale.has(token)) continue;
        const n = parseFloat(token);
        if (!Number.isFinite(n)) continue;
        if (span.min != null && n < span.min) continue;
        if (span.max != null && n > span.max) continue;
        if (n >= span.lo && n <= span.hi) return true;
    }
    return false;
}

/** Whether a respondent meets one condition, under the rules the variable was saved with. */
export function evalCondition(
    resp: Answers,
    cond: ResolvedCondition,
    rules: ConditionRules = LEGACY_RULES,
): boolean {
    // A condition read from JSON (DANI writes variables too) may be malformed: it counts no one.
    const qid = typeof cond?.questionId === 'string' ? cond.questionId : '';
    const selected = resp[qid.toUpperCase()];
    if (!selected || selected.size === 0) {
        // Did not answer. Only "not X" (≠, none of) can hold, and only where the base is everyone:
        // under the earlier rules, or for a variable whose base was chosen as everyone.
        return isNegative(cond.operator) && rules.everyone;
    }
    const val = String(cond.value ?? '')
        .trim()
        .toUpperCase();
    const holds = () =>
        cond.anyOf ? cond.anyOf.some((id) => selected.has(id)) : selected.has(val);
    // `in` / `notIn` (X46): any of the answers picked, each with its merged-wave codes once resolved.
    const holdsAny = () =>
        cond.anyOf
            ? cond.anyOf.some((id) => selected.has(id))
            : valuesOf(cond).some((v) => selected.has(v.toUpperCase()));
    switch (cond.operator) {
        case 'eq':
            return holds();
        case 'neq':
            return !holds();
        case 'in':
            return holdsAny();
        case 'notIn':
            return valuesOf(cond).length > 0 && !holdsAny();
        case 'lt':
        case 'gt':
        case 'lte':
        case 'gte':
            return rules.current
                ? currentComparison(selected, cond)
                : legacyComparison(selected, cond);
        case 'between':
            return rules.current ? currentSpan(selected, cond) : legacySpan(selected, cond);
        default:
            return false;
    }
}

/** The questions a list of conditions names, upper-cased. */
function questionsOf(conditions: readonly VariableCondition[]): string[] {
    return conditions
        .map((c) => (typeof c?.questionId === 'string' ? c.questionId.trim().toUpperCase() : ''))
        .filter(Boolean);
}

/**
 * X46: one group. NOT ( … ) under today's rules holds only for someone who answered one of the
 * group's questions, so "NOT (Aware)" never counts someone who was never asked, just as ≠ does not.
 * Where the base is everyone, and under the earlier rules, it is plain negation.
 */
function evalGroup(
    resp: Answers,
    group: BucketGroup<ResolvedCondition>,
    rules: ConditionRules,
): boolean {
    const matched =
        group.join === 'all'
            ? group.conditions.every((c) => evalCondition(resp, c, rules))
            : group.conditions.some((c) => evalCondition(resp, c, rules));
    if (!group.not) return matched;
    if (!rules.everyone && !answeredAny(resp, questionsOf(group.conditions))) return false;
    return !matched;
}

/**
 * Whether a respondent is in a bucket. A grouped bucket (X46) reads its groups: each joined within
 * by its own AND / OR and negated where it says NOT, and the groups joined by `groupJoin`.
 * Otherwise the conditions fold left to right: each condition's `nextJoin` says how it combines
 * with the next, falling back to the bucket's `join`.
 */
export function evalBucket(
    resp: Answers,
    bucket: VariableBucket,
    rules: ConditionRules = LEGACY_RULES,
): boolean {
    if (!Array.isArray(bucket?.conditions) || bucket.conditions.length === 0) return false;
    const groups = bucketGroups<ResolvedCondition>(bucket);
    if (groups) {
        if (groups.length === 0) return false;
        return groupJoinOf(bucket) === 'any'
            ? groups.some((g) => evalGroup(resp, g, rules))
            : groups.every((g) => evalGroup(resp, g, rules));
    }
    let result = evalCondition(resp, bucket.conditions[0], rules);
    for (let i = 0; i < bucket.conditions.length - 1; i++) {
        const op = bucket.conditions[i].nextJoin ?? bucket.join;
        const next = evalCondition(resp, bucket.conditions[i + 1], rules);
        result = op === 'all' ? result && next : result || next;
    }
    return result;
}

/** The first number a respondent holds for a question, the reading Formula uses (a diary person
 *  is read by `personNumber` first). Bucketing reads any entry's number (`evalRange`). */
export function firstNumberOf(selected: Set<string> | undefined): number | null {
    if (!selected || selected.size === 0) return null;
    const num = parseFloat([...selected][0]);
    return isNaN(num) ? null : num;
}

/**
 * Whether a respondent's answer to `sourceQid` falls in a Bucketing range (inclusive).
 *
 * M4S-5777 (diary D3; round-2 I1): a person is in every range any of their answers falls in when
 * the cell is a diary person's merged cell, or the variable was saved under today's rules
 * (`rules.current`). An unstamped variable on an ordinary cell keeps the first number it holds,
 * the reading it was saved with. On a cell with one answer the two readings agree.
 */
export function evalRange(
    resp: Answers,
    sourceQid: string,
    range: RangeSegment,
    rules: ConditionRules = LEGACY_RULES,
): boolean {
    const selected = resp[sourceQid.toUpperCase()];
    if (!selected || selected.size === 0) return false;
    const min = range.min !== '' ? parseFloat(range.min) : -Infinity;
    const max = range.max !== '' ? parseFloat(range.max) : Infinity;
    if (rules.current || entryCellOf(selected)) {
        return anyNumber(selected, (num) => num >= min && num <= max);
    }
    const num = firstNumberOf(selected);
    return num != null && num >= min && num <= max;
}

// ── Coverage of number ranges (X49) ───────────────────────────────────────────

/** Where a Bucketing variable's number ranges leave answers out, or count one twice. */
export interface RangeCoverage {
    /** Pairs of ranges (indexes into the list given) that share a value. */
    overlaps: [number, number][];
    /**
     * Stretches no range counts, in order: `from` / `to` are the numbers either side of the gap
     * (the end of one range, the start of the next), or the scale's ends.
     */
    gaps: { from: number; to: number; kind: 'between' | 'below' | 'above' }[];
}

const fmt = (n: number) => String(Math.round(n * 1e6) / 1e6);

/**
 * X49: the gaps and overlaps of number ranges, both bounds inclusive as `evalRange` reads them.
 * `integer`: the question's answers are whole numbers (a scale), so 18–24 then 25–34 leaves no
 * gap; otherwise 24.5 falls between them. `scale`: the question's own ends, when known, so a first
 * range starting above the scale's minimum (or a last one ending below its maximum) is a gap too.
 * A range with a bound that is not a number is left out. Pure; the dialog shows the result as a
 * warning, never a block: a gap may be meant.
 */
export function rangeCoverage(
    ranges: readonly Pick<RangeSegment, 'min' | 'max'>[],
    opts: { integer?: boolean; scale?: { min: number | null; max: number | null } } = {},
): RangeCoverage {
    const step = opts.integer ? 1 : 0;
    const parsed = ranges
        .map((r, i) => ({
            i,
            min: r.min.trim() !== '' ? parseFloat(r.min) : -Infinity,
            max: r.max.trim() !== '' ? parseFloat(r.max) : Infinity,
        }))
        .filter((r) => !isNaN(r.min) && !isNaN(r.max) && r.min <= r.max);
    const overlaps: [number, number][] = [];
    for (let a = 0; a < parsed.length; a++) {
        for (let b = a + 1; b < parsed.length; b++) {
            if (parsed[a].min <= parsed[b].max && parsed[b].min <= parsed[a].max) {
                overlaps.push([parsed[a].i, parsed[b].i]);
            }
        }
    }
    const gaps: RangeCoverage['gaps'] = [];
    const sorted = [...parsed].sort((a, b) => a.min - b.min || a.max - b.max);
    // Walk the ranges in order, keeping the furthest end reached so far.
    let reach = -Infinity;
    sorted.forEach((r, k) => {
        if (k > 0 && Number.isFinite(reach) && Number.isFinite(r.min) && r.min - reach > step) {
            gaps.push({ from: reach, to: r.min, kind: 'between' });
        }
        reach = Math.max(reach, r.max);
    });
    const lo = opts.scale?.min;
    const hi = opts.scale?.max;
    if (sorted.length && lo != null && Number.isFinite(sorted[0].min) && sorted[0].min > lo) {
        gaps.unshift({ from: lo, to: sorted[0].min, kind: 'below' });
    }
    if (sorted.length && hi != null && Number.isFinite(reach) && reach < hi) {
        gaps.push({ from: reach, to: hi, kind: 'above' });
    }
    return { overlaps, gaps };
}

/** The warnings the Bucketing tab shows for a coverage, one line each. */
export function rangeCoverageLines(
    coverage: RangeCoverage,
    labels: readonly string[],
    opts: { integer?: boolean } = {},
): string[] {
    const name = (i: number) => (labels[i]?.trim() ? `"${labels[i].trim()}"` : `range ${i + 1}`);
    const lines: string[] = [];
    for (const [a, b] of coverage.overlaps) {
        lines.push(`${name(a)} and ${name(b)} share values, so someone can count in both.`);
    }
    const span = (a: number, b: number) =>
        a === b ? `No range counts ${fmt(a)}.` : `No range counts ${fmt(a)} to ${fmt(b)}.`;
    for (const g of coverage.gaps) {
        if (g.kind === 'below') {
            lines.push(
                opts.integer
                    ? span(g.from, g.to - 1)
                    : `No range counts answers below ${fmt(g.to)} (the scale starts at ${fmt(g.from)}).`,
            );
        } else if (g.kind === 'above') {
            lines.push(
                opts.integer
                    ? span(g.from + 1, g.to)
                    : `No range counts answers above ${fmt(g.from)} (the scale ends at ${fmt(g.to)}).`,
            );
        } else if (opts.integer && g.to - g.from === 2) {
            lines.push(`No range counts ${fmt(g.from + 1)}.`);
        } else if (opts.integer) {
            lines.push(`No range counts ${fmt(g.from + 1)} to ${fmt(g.to - 1)}.`);
        } else {
            lines.push(
                `No range counts answers above ${fmt(g.from)} and below ${fmt(g.to)} (for example ${fmt((g.from + g.to) / 2)}).`,
            );
        }
    }
    return lines;
}

// ── The base ──────────────────────────────────────────────────────────────────

/**
 * The questions a variable's own table is based on (decision C1: answered ANY of them). Survey /
 * Wave is answered by everyone, so it narrows nothing and is left out. Upper-cased, distinct.
 */
export function baseQuestionsOf(cv: CustomVariable): string[] {
    const qids =
        cv.kind === 'bucketing'
            ? [cv.sourceQuestionId]
            : cv.buckets.flatMap((b) => b.conditions.map((c) => c.questionId));
    const out: string[] = [];
    for (const raw of qids) {
        const q = raw?.trim().toUpperCase();
        if (q && q !== '__SURVEY_ID' && !out.includes(q)) out.push(q);
    }
    return out;
}

/** Whether a respondent answered any of `qids`. No question at all reads as answered. */
export function answeredAny(resp: Answers, qids: readonly string[]): boolean {
    if (qids.length === 0) return true;
    return qids.some((q) => (resp[q]?.size ?? 0) > 0);
}

/** The line a variable's own table prints about its base. */
export function baseNoteOf(cv: CustomVariable): string {
    const qids = baseQuestionsOf(cv);
    const named = qids.length === 1 ? qids[0] : `any of ${qids.join(', ')}`;
    if (variableBaseOf(cv) === 'everyone') {
        return qids.length
            ? `Base: everyone in the column, including people who did not answer ${named}.`
            : 'Base: everyone in the column.';
    }
    return qids.length ? `Base: people who answered ${named}.` : 'Base: everyone in the column.';
}

export const OVERLAPPING_ROWS_NOTE = 'Rows can overlap, so they do not add up to the base.';

// ── Saved variables under the earlier rules ───────────────────────────────────

/**
 * Why a saved variable would count differently under today's rules. `formula` (X51): a Formula
 * saved before scores read answer values (compute reports it; `legacyRulesOf` does not read
 * formulas).
 */
export type LegacyReason = 'not-asked' | 'codes' | 'base' | 'formula';

export interface LegacyRule {
    cvId: string;
    name: string;
    reasons: LegacyReason[];
}

const REASON_TEXT: Record<LegacyReason, string> = {
    'not-asked': 'a "not" condition counts people who were not asked',
    codes: 'a comparison reads answer codes, not the order or value of the answers',
    base: 'its table is based on everyone, including people who were not asked',
    formula:
        "its score adds answer codes (Don't know included), not the answers' values, and its base counts people with no score",
};

export function legacyReasonText(reason: LegacyReason): string {
    return REASON_TEXT[reason];
}

/** One line per variable, for the table's notes and the workspace's flag. */
export function legacyRuleLine(rule: LegacyRule): string {
    return `"${rule.name}" uses the earlier condition rules: ${rule.reasons.map(legacyReasonText).join('; ')}. Edit and save it to apply the current rules.`;
}

/** The workspace's flag when a saved crosstab opens with variables under the earlier rules. */
export function legacyRulesNotice(rules: readonly LegacyRule[]): string {
    if (rules.length === 1) {
        return `${legacyRuleLine(rules[0])} Its numbers are shown as they were saved.`;
    }
    const names = rules.map((r) => `"${r.name}"`).join(', ');
    return `${rules.length} saved variables use the earlier condition rules (${names}), so ≠ may count people who were not asked, or a comparison may read answer codes. Their numbers are shown as they were saved. Edit and save each one to apply the current rules.`;
}

/**
 * The saved variables in use whose numbers would move under today's rules, and why. A variable is
 * reported when its members would change (a ≠ that counts the unasked, or a comparison on a coded
 * list), or, rendered as its own table, when its base would (someone in Total answered none of its
 * questions). A variable whose numbers would not move is not reported, so an equality-only banner
 * saved before is never flagged.
 *
 * `variables` are resolved (`resolveConditions`). `domain` is the respondents the table shows.
 */
export function legacyRulesOf(args: {
    variables: ReadonlyArray<{ cv: CustomVariable; standalone: boolean }>;
    responses: ReadonlyArray<Answers>;
    domain: readonly number[];
}): LegacyRule[] {
    const out: LegacyRule[] = [];
    for (const { cv, standalone } of args.variables) {
        if (isCurrentRules(cv)) continue;
        if (cv.kind === 'formula' || cv.kind === 'combine') continue;
        const reasons: LegacyReason[] = [];
        if (cv.kind === 'logic' || cv.kind === 'categorical-bucketing') {
            const conds = cv.buckets.flatMap((b) => b.conditions);
            // X46: none-of and a NOT group read the unasked as ≠ does; a range reads codes as a
            // comparison does.
            const hasNeq =
                conds.some((c) => isNegative(c.operator)) || cv.buckets.some(hasNotGroup);
            const hasCmp = conds.some((c) => readsOrderOrValue(c.operator));
            // Equality alone means the same under both rules, so only these can move members.
            const moves =
                (hasNeq || hasCmp) &&
                cv.buckets.some((b) =>
                    args.domain.some(
                        (i) =>
                            evalBucket(args.responses[i], b, LEGACY_RULES) !==
                            evalBucket(args.responses[i], b, CURRENT_RULES),
                    ),
                );
            if (moves && hasNeq) reasons.push('not-asked');
            if (moves && hasCmp) reasons.push('codes');
        }
        if (standalone) {
            const qids = baseQuestionsOf(cv);
            if (args.domain.some((i) => !answeredAny(args.responses[i], qids))) {
                reasons.push('base');
            }
        }
        if (reasons.length) out.push({ cvId: cv.id, name: cv.name, reasons });
    }
    return out;
}

// ── Saving ────────────────────────────────────────────────────────────────────

/**
 * Why a variable's conditions cannot be saved under today's rules, or null. A comparison on a coded
 * list must name one of its answers on the order (never a typed code); on a number question it
 * must be a number; and a question that takes no comparison takes none.
 */
export function comparisonBlocker(
    buckets: readonly VariableBucket[],
    find: (
        qid: string,
    ) => (ComparisonQuestion & { answers: { id: string; label: string }[] }) | undefined,
): string | null {
    for (const b of buckets) {
        for (const c of b.conditions) {
            if (!c.questionId) continue;
            if (c.operator === 'between') {
                const q = find(c.questionId);
                const kind = comparisonKindOf(q);
                const name = q?.qid ?? c.questionId;
                if (kind === 'none') return `${name} takes no range. Use = or ≠.`;
                const bounds = [boundOf(c.min), boundOf(c.max)].filter(Boolean);
                if (bounds.length === 0) return `Set at least one end of the range on ${name}.`;
                if (kind === 'order') {
                    const onOrder = new Set(
                        orderedAnswers(q?.answers ?? []).map((a) => a.id.toUpperCase()),
                    );
                    if (bounds.some((v) => !onOrder.has(v.toUpperCase()))) {
                        return `Pick the ends of the range on ${name} from its list.`;
                    }
                } else if (bounds.some((v) => !Number.isFinite(parseFloat(v)))) {
                    return `Type the numbers the range on ${name} runs between.`;
                }
                continue;
            }
            if (!isComparison(c.operator)) continue;
            const q = find(c.questionId);
            const kind = comparisonKindOf(q);
            const name = q?.qid ?? c.questionId;
            if (kind === 'none') {
                return `${name} cannot be compared with ${operatorLabel(c.operator, 'value')}. Use = or ≠.`;
            }
            if (kind === 'order') {
                const ok = orderedAnswers(q?.answers ?? []).some(
                    (a) => a.id.toUpperCase() === c.value.trim().toUpperCase(),
                );
                if (!ok) return `Pick the answer ${name} is compared with from its list.`;
            } else if (!Number.isFinite(parseFloat(c.value))) {
                return `Type the number ${name} is compared with.`;
            }
        }
    }
    return null;
}

/**
 * The conditions an AI banner plan's column makes from one operator and several answer codes. A
 * comparison is ONE condition (several ANDed comparisons were incoherent): ≥ / > take the answer
 * earliest on the order (or the lowest number), ≤ / < the latest (or the highest). A code that is
 * not one of the question's answers on the order is dropped, and a comparison with none left
 * makes no condition, so the plan's column is refused rather than counting everyone. Equality
 * conditions are returned one per code, as before.
 */
export function planConditions(
    q: CrosstabQuestionDto | undefined,
    qid: string,
    operator: VariableCondition['operator'],
    values: readonly string[],
): VariableCondition[] {
    const codes = values.map((v) => String(v).trim()).filter(Boolean);
    if (!isComparison(operator))
        return codes.map((value) => ({ questionId: qid, operator, value }));
    const kind = comparisonKindOf(q);
    if (!q || kind === 'none') return [];
    const low = operator === 'gt' || operator === 'gte';
    if (kind === 'value') {
        const nums = codes.map(parseFloat).filter(Number.isFinite);
        if (!nums.length) return [];
        const pick = low ? Math.min(...nums) : Math.max(...nums);
        return [{ questionId: qid, operator, value: String(pick) }];
    }
    const ranks = rankMapOf(q);
    const ranked = codes
        .map((value) => ({ value, rank: ranks.get(value.toUpperCase()) }))
        .filter((x): x is { value: string; rank: number } => x.rank != null);
    if (!ranked.length) return [];
    ranked.sort((a, b) => a.rank - b.rank);
    const pick = low ? ranked[0] : ranked[ranked.length - 1];
    return [{ questionId: qid, operator, value: pick.value }];
}
